import React from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Check, Info, AlertCircle } from 'lucide-react';
import './Alert.css';

export type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

export interface AlertProps {
  show: boolean;
  title: string;
  message: string;
  type?: AlertType;
  onClose: () => void;
  primaryButtonText?: string;
  onPrimaryClick?: () => void;
  secondaryButtonText?: string;
  onSecondaryClick?: () => void;
}

const Alert: React.FC<AlertProps> = ({
  show,
  title,
  message,
  type = 'info',
  onClose,
  primaryButtonText = 'Confirm',
  onPrimaryClick,
  secondaryButtonText,
  onSecondaryClick
}) => {
  if (!show) return null;

  if (type === 'confirm') {
    return ReactDOM.createPortal(
      <div className="iqadmin-alert-overlay" onClick={onClose}>
        <div className="iqadmin-alert-modal type-confirm" onClick={(e) => e.stopPropagation()}>
          <div className="iqadmin-alert-confirm-top">
            <h3 className="iqadmin-alert-confirm-title">{title || 'Confirmation'}</h3>
          </div>

          <div className="iqadmin-alert-bottom">
            <p className="iqadmin-alert-message" dangerouslySetInnerHTML={{ __html: message }}></p>

            <div className="iqadmin-alert-actions center">
              <button className="iqadmin-alert-btn confirm-yes" onClick={onPrimaryClick || onClose}>
                {primaryButtonText || 'Yes'}
              </button>
              {secondaryButtonText && (
                <button className="iqadmin-alert-btn confirm-no" onClick={onSecondaryClick || onClose}>
                  {secondaryButtonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const getIcon = () => {
    switch (type) {
      case 'success': return <Check size={32} />;
      case 'error': return <AlertCircle size={32} />;
      case 'warning': return <AlertTriangle size={32} />;
      case 'info':
      default: return <Info size={32} />;
    }
  };

  return ReactDOM.createPortal(
    <div className="iqadmin-alert-overlay">
      <div className={`iqadmin-alert-modal type-${type}`}>
        <div className="iqadmin-alert-top">
          <div className="iqadmin-alert-icon-wrap">
            {getIcon()}
          </div>
        </div>

        <div className="iqadmin-alert-bottom">
          <h2 className="iqadmin-alert-title">{title}</h2>
          <p className="iqadmin-alert-message" dangerouslySetInnerHTML={{ __html: message }}></p>

          <div className="iqadmin-alert-actions">
            {secondaryButtonText && (
              <button className="iqadmin-alert-btn secondary" onClick={onSecondaryClick || onClose}>
                {secondaryButtonText}
              </button>
            )}
            <button className="iqadmin-alert-btn primary" onClick={onPrimaryClick || onClose}>
              {primaryButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Alert;
